import type { ReactNode } from 'react'
import { EventNames } from '../../../../../../../types/misc.types'
import {
  lightImpactVibrate,
  openEliteLandingPage,
  openPrimeLandingPage,
  trackEvent
} from '../../../../../../../utils/appBridge'
import {
  PlansLumpsumListEventIds,
  PlansScreen,
  PlansSipListEventIds
} from '../../../../analytics/plansEvents'
import RenderHTML from '../../../../../../../components/ui-components/RenderHTML'

type UnlockLandingPage = 'elite' | 'prime'

function isSipUnlockScreen(analyticsScreen: string): boolean {
  return (
    analyticsScreen.startsWith('Plans SIP') ||
    analyticsScreen === PlansScreen.SipYourPlan ||
    analyticsScreen === PlansScreen.SipIdeal
  )
}

/** Blurred list + lock card: starts and sticks at top-20 while the list is in view. */
export function LockedBlurredListOverlay({
  message,
  buttonLabel,
  children,
  landingPage = 'elite',
  analyticsScreen = PlansScreen.LumpsumAllocation
}: {
  message: ReactNode
  buttonLabel: string
  children: ReactNode
  landingPage?: UnlockLandingPage
  analyticsScreen?: string
}) {
  return (
    <div className="relative">
      <div className="pointer-events-none select-none opacity-70 blur-[6px]" aria-hidden>
        {children}
      </div>
      {/* pt matches sticky top so the card isn’t flush to the list top before stick. */}
      <div className="pointer-events-none absolute inset-0 py-20">
        <div className="sticky top-20 z-10 flex justify-center px-5">
          <div className="pointer-events-auto">
            <LockedUnlockCard
              message={message}
              buttonLabel={buttonLabel}
              landingPage={landingPage}
              analyticsScreen={analyticsScreen}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/** Blur-overlay unlock card shared by Allocation / Ideal / Rebalance locked bodies. */
export function LockedUnlockCard({
  message,
  buttonLabel,
  landingPage = 'elite',
  analyticsScreen = PlansScreen.LumpsumAllocation
}: {
  message: ReactNode
  buttonLabel: string
  landingPage?: UnlockLandingPage
  analyticsScreen?: string
}) {
  return (
    <div className="w-full min-w-[246px] max-w-[320px] rounded-[24px] border border-black/10 bg-white px-5 py-6 shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
      <div className="flex justify-center pb-4">
        <RenderHTML innerHTMLText={LockIcon} />
      </div>
      <div className="pb-5 text-center text-[16px] font-[500] leading-[140%] text-[#232323]">
        {message}
      </div>
      <LockedUnlockGradientButton
        label={buttonLabel}
        isFullWidth
        heightPx={48}
        landingPage={landingPage}
        analyticsScreen={analyticsScreen}
      />
    </div>
  )
}

/** Empty-state upsell when rolled out but subscription-locked and list has no funds. */
export function LockedEmptyUpsell({
  copy,
  buttonLabel = 'Unlock with Elite',
  icon = 'default',
  landingPage = 'elite',
  analyticsScreen = PlansScreen.LumpsumAllocation
}: {
  copy: string
  buttonLabel?: string
  /** Rebalance uses laurel+#1; allocation / SIP / others use the pie-chart wreath. */
  icon?: 'default' | 'rebalance'
  landingPage?: UnlockLandingPage
  analyticsScreen?: string
}) {
  return (
    <div className="flex flex-col items-center gap-6 bg-white px-4 pt-[84px] pb-[120px]">
      <div className="shrink-0" aria-hidden>
        <RenderHTML
          innerHTMLText={icon === 'rebalance' ? UpSellRebalanceIcon : UpSellIcon}
        />
      </div>
      <div className="w-full text-center text-[14px] text-[#232323]">
        <span className="block font-[500] leading-[140%]">
          Always keep your portfolio in top form.
        </span>
        <RenderHTML innerHTMLText={copy} className="font-[400] leading-[150%]" />
      </div>
      <LockedUnlockGradientButton
        label={buttonLabel}
        heightPx={40}
        landingPage={landingPage}
        analyticsScreen={analyticsScreen}
      />
    </div>
  )
}

/** Radial-gradient unlock CTA shared by blur card and empty upsell. */
function LockedUnlockGradientButton({
  label,
  isFullWidth = false,
  heightPx,
  landingPage,
  analyticsScreen
}: {
  label: string
  isFullWidth?: boolean
  heightPx: 40 | 48
  landingPage: UnlockLandingPage
  analyticsScreen: string
}) {
  return (
    <button
      type="button"
      aria-label={label}
      className={`flex shrink-0 items-center justify-center rounded-2xl border-none px-8 text-[14px] font-[500] leading-[120%] text-white outline-none focus:outline-none${isFullWidth ? ' w-full' : ''
        }${heightPx === 48 ? ' h-12' : ' h-10'}`}
      style={{
        backgroundImage:
          'radial-gradient(106.16px 19.94px at 50% 100%, #45372D 0%, #232323 100%)',
        backgroundColor: '#232323'
      }}
      onClick={() => {
        lightImpactVibrate()
        trackEvent({
          eventName: EventNames.ButtonTap,
          payload: {
            ScreenName: analyticsScreen,
            eventId: isSipUnlockScreen(analyticsScreen)
              ? PlansSipListEventIds.UnlockElite
              : PlansLumpsumListEventIds.UnlockElite,
            buttonLabel: label
          }
        })
        if (landingPage === 'prime') {
          openPrimeLandingPage()
          return
        }
        openEliteLandingPage()
      }}
    >
      {label}
    </button>
  )
}

export const LockIcon = `<svg width="56" height="56" viewBox="0 0 56 56" fill="none" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
<path d="M33.825 26.0586H22.17C21.4548 26.0586 20.875 26.6384 20.875 27.3536V35.1236C20.875 35.8388 21.4548 36.4186 22.17 36.4186H33.825C34.5402 36.4186 35.12 35.8388 35.12 35.1236V27.3536C35.12 26.6384 34.5402 26.0586 33.825 26.0586Z" fill="white" stroke="#232323" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M32.5298 26.057V24.1145C32.5298 22.9124 32.0524 21.7596 31.2023 20.9096C30.3523 20.0596 29.1994 19.582 27.9973 19.582C26.7952 19.582 25.6424 20.0596 24.7924 20.9096C23.9424 21.7596 23.4648 22.9124 23.4648 24.1145V26.057" stroke="#232323" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round"/>
<g opacity="0.2" filter="url(#filter0_f_96108_308893)">
<g id="Repeat group 1_inner" data-figma-trr="r11p30.8-0f">
<circle cx="28.0562" cy="12.6578" r="1.4" fill="#232323"/>
</g>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(19.6229 -10.7145) rotate(32.7273)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(41.9233 -9.1191) rotate(65.4545)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(59.8212 4.27954) rotate(98.1818)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(67.634 25.2275) rotate(130.909)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(62.8812 47.074) rotate(163.636)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(47.0718 62.8828) rotate(-163.636)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(25.2252 67.6348) rotate(-130.909)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(4.27747 59.8213) rotate(-98.1818)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(-9.12056 41.923) rotate(-65.4546)"/>
<use xlink:href="#Repeat%20group%201_inner" transform="translate(-10.7151 19.6225) rotate(-32.7273)"/>
</g>
<g opacity="0.3" filter="url(#filter1_f_96108_308893)">
<g id="Repeat group 2_inner" data-figma-trr="r16p47.6-0f">
<circle cx="28.0406" cy="4.23984" r="1.4" fill="#232323"/>
</g>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(12.8648 -8.59629) rotate(22.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(28.0399 -11.6151) rotate(45)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(43.2152 -8.59682) rotate(67.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(56.0803 -0.000974823) rotate(90)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(64.6765 12.8638) rotate(112.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(67.6953 28.0389) rotate(135)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(64.6771 43.2142) rotate(157.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(56.0812 56.0793) rotate(-180)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(43.2165 64.6756) rotate(-157.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(28.0413 67.6944) rotate(-135)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(12.866 64.6761) rotate(-112.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(0.000975714 56.0803) rotate(-90)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(-8.59532 43.2155) rotate(-67.5)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(-11.6141 28.0403) rotate(-45)"/>
<use xlink:href="#Repeat%20group%202_inner" transform="translate(-8.59584 12.8651) rotate(-22.5)"/>
</g>
<defs>
<filter id="filter0_f_96108_308893" x="9.79824" y="9.79922" width="36.5163" height="36.5148" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
<feGaussianBlur stdDeviation="0.7" result="effect1_foregroundBlur_96108_308893"/>
</filter>
<filter id="filter1_f_96108_308893" x="2.10176" y="2.10078" width="51.8775" height="51.8766" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
<feFlood flood-opacity="0" result="BackgroundImageFix"/>
<feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
<feGaussianBlur stdDeviation="0.35" result="effect1_foregroundBlur_96108_308893"/>
</filter>
</defs>
</svg>
`

export const UpSellIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48" fill="none">
  <path d="M10.9521 34.8501C11.3287 34.0655 12.2249 33.7591 12.9531 34.1646C13.6808 34.5707 13.966 35.5367 13.5898 36.3218L12.3368 38.9351C14.5262 39.4706 16.5274 40.7443 18.1572 42.4917C18.7385 43.115 18.7409 44.1285 18.163 44.7554C17.5848 45.3819 16.6447 45.3846 16.0634 44.7612C14.3425 42.9164 12.2216 41.8663 10.0654 41.856L4.75677 42.0747C3.9377 42.108 3.24907 41.4177 3.21771 40.5347C3.18709 39.6517 3.82549 38.9083 4.64447 38.8745L9.11029 38.6919L10.9521 34.8501ZM35.0497 34.1646C35.778 33.7591 36.674 34.0655 37.0507 34.8501L38.8925 38.6919L43.3583 38.8745C44.1775 38.9082 44.8158 39.6517 44.7851 40.5347C44.7538 41.4177 44.065 42.108 43.246 42.0747L37.9374 41.856C35.7813 41.8663 33.6603 42.9164 31.9394 44.7612C31.3581 45.3846 30.418 45.382 29.8398 44.7554C29.2619 44.1285 29.2644 43.115 29.8456 42.4917C31.4754 40.7443 33.4767 39.4706 35.666 38.9351L34.413 36.3218C34.0369 35.5367 34.322 34.5707 35.0497 34.1646ZM23.1962 27.8716C23.1962 28.0931 23.2874 28.3054 23.4482 28.4575L29.9667 34.6235C28.3786 36.0334 26.2887 36.8892 23.9999 36.8892C19.0295 36.889 14.9999 32.8519 14.9999 27.8716C15 23.1627 18.6023 19.2966 23.1962 18.8892V27.8716ZM8.11615 31.7544C8.8223 31.3062 9.73209 31.5607 10.1484 32.3218C10.5646 33.0831 10.3308 34.065 9.62493 34.5142L6.41888 36.5513C6.01964 36.8048 5.53343 36.8417 5.10443 36.6528L1.25579 34.9605C0.496821 34.6264 0.132887 33.6919 0.442317 32.8735C0.75228 32.0554 1.61793 31.6627 2.37689 31.9966L5.54583 33.3892L8.11615 31.7544ZM37.8544 32.3218C38.2707 31.5607 39.1805 31.3064 39.8867 31.7544L42.4579 33.3892L45.6259 31.9966C46.385 31.6626 47.2516 32.0552 47.5615 32.8735C47.8711 33.6921 47.506 34.6264 46.747 34.9605L42.8984 36.6528C42.4693 36.8417 41.9831 36.8049 41.5839 36.5513L38.3788 34.5142C37.6726 34.0651 37.4383 33.0833 37.8544 32.3218ZM24.8036 18.8892C29.3977 19.2965 32.9999 23.1626 32.9999 27.8716C32.9999 29.9788 32.2791 31.9175 31.0702 33.4526L24.8036 27.5249V18.8892ZM0.993099 24.9448C1.52823 24.2756 2.46577 24.1995 3.08685 24.7759L5.48724 27.0064L8.39544 25.5562C9.1395 25.185 10.0227 25.5334 10.3671 26.3355C10.7112 27.1377 10.3872 28.0901 9.64349 28.4614L5.86615 30.3452C5.33773 30.609 4.71461 30.5155 4.27337 30.106L1.14935 27.2026C0.52846 26.6257 0.458404 25.6145 0.993099 24.9448ZM44.9169 24.7759C45.538 24.1998 46.4747 24.2758 47.0097 24.9448C47.5444 25.6145 47.4744 26.6257 46.8535 27.2026L43.7294 30.106C43.2882 30.5155 42.6652 30.609 42.1367 30.3452L38.3593 28.4614C37.6156 28.0901 37.2922 27.1378 37.6367 26.3355C37.9809 25.5333 38.8642 25.1849 39.6083 25.5562L42.5156 27.0064L44.9169 24.7759ZM3.50775 18.7251C4.10233 18.1166 5.04282 18.1428 5.60736 18.7837L7.20208 20.5952L9.87591 19.7886C10.6652 19.5506 11.4838 20.0479 11.705 20.8989C11.926 21.7502 11.4651 22.6321 10.6757 22.8706L7.14251 23.938C6.6129 24.0979 6.0438 23.9304 5.66497 23.5005L3.45404 20.9888C2.88969 20.3477 2.91338 19.3338 3.50775 18.7251ZM42.3954 18.7837C42.9598 18.1428 43.9004 18.1166 44.4951 18.7251C45.0894 19.3337 45.1139 20.3476 44.5497 20.9888L42.3378 23.5005C41.9589 23.9304 41.3898 24.0979 40.8603 23.938L37.3271 22.8706C36.5376 22.6321 36.0776 21.7501 36.2988 20.8989C36.52 20.0481 37.3378 19.5507 38.1269 19.7886L40.8007 20.5952L42.3954 18.7837ZM9.42181 14.7583C9.85368 14.0071 10.7679 13.7745 11.4648 14.2398C12.1617 14.7053 12.3759 15.6914 11.9443 16.4429L11.2519 17.6499C10.8202 18.4008 9.90572 18.6332 9.20892 18.1685C8.51194 17.7028 8.29567 16.7149 8.72747 15.9634L9.42181 14.7583ZM36.538 14.2398C37.2349 13.7744 38.1491 14.0072 38.581 14.7583L39.2753 15.9634C39.7071 16.7149 39.4909 17.7028 38.7939 18.1685C38.0972 18.6331 37.1827 18.4009 36.7509 17.6499L36.0585 16.4429C35.627 15.6914 35.8412 14.7054 36.538 14.2398Z" fill="url(#paint0_linear_103495_63583)"/>
  <defs>
    <linearGradient id="paint0_linear_103495_63583" x1="24.0018" y1="14" x2="24.0018" y2="45.2274" gradientUnits="userSpaceOnUse">
      <stop stop-color="#232323"/>
      <stop offset="1" stop-color="#5C3939"/>
    </linearGradient>
  </defs>
</svg>`

export const UpSellRebalanceIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="45" height="29" viewBox="0 0 45 29" fill="none">
  <path d="M9.94067 19.5147C10.2932 18.7804 11.1318 18.4933 11.8133 18.8729C12.4945 19.253 12.7607 20.1572 12.4086 20.892L11.2362 23.3385C13.2853 23.8398 15.1588 25.0312 16.6842 26.6667C17.2282 27.25 17.2305 28.1986 16.6896 28.7853C16.1484 29.372 15.2688 29.3746 14.7246 28.7911C13.1139 27.0643 11.1284 26.0812 9.11017 26.0716L4.14167 26.2766C3.37493 26.3077 2.73055 25.6615 2.70141 24.8348C2.67275 24.0084 3.27014 23.3134 4.03671 23.2818L8.21633 23.1102L9.94067 19.5147ZM32.4943 18.8729C33.1759 18.4933 34.0145 18.7804 34.3671 19.5147L36.0913 23.1102L40.271 23.2818C41.0377 23.3134 41.635 24.0084 41.6063 24.8348C41.5772 25.6615 40.9328 26.3077 40.1661 26.2766L35.1975 26.0716C33.1793 26.0812 31.1938 27.0643 29.583 28.7911C29.0388 29.3746 28.1593 29.372 27.6181 28.7853C27.0773 28.1986 27.0795 27.25 27.6235 26.6667C29.1489 25.0312 31.0224 23.8398 33.0716 23.3385L31.899 20.892C31.547 20.1572 31.8132 19.253 32.4943 18.8729ZM22.5917 2.69224C23.3591 2.69224 23.9813 3.36305 23.9813 4.19052V18.6973H26.4837C27.2509 18.6975 27.8733 19.3683 27.8733 20.1956C27.8733 21.023 27.2509 21.6936 26.4837 21.6938H22.7581C22.7036 21.7008 22.648 21.7056 22.5917 21.7056C22.5359 21.7056 22.4811 21.7008 22.427 21.6938H18.7449C17.9775 21.6938 17.3554 21.023 17.3554 20.1956C17.3554 19.3681 17.9775 18.6973 18.7449 18.6973H21.2021V9.21405C20.693 9.50613 20.1294 9.69184 19.5339 9.69202H18.5134C17.7459 9.69202 17.1238 9.02121 17.1238 8.19374C17.1242 7.36663 17.7463 6.69546 18.5134 6.69546H19.5339C19.6878 6.69493 20.0995 6.54891 20.5346 6.00486C20.9437 5.49269 21.2021 4.82763 21.2021 4.19052C21.2021 3.36311 21.8243 2.69234 22.5917 2.69224ZM7.28633 16.6177C7.94725 16.1981 8.79835 16.4359 9.18797 17.1483C9.57774 17.861 9.35865 18.7804 8.69763 19.2007L5.69771 21.1067C5.32397 21.3442 4.86902 21.379 4.46735 21.2022L0.864911 19.6182C0.154544 19.3056 -0.186443 18.4312 0.10317 17.6653C0.393119 16.8991 1.20382 16.5314 1.91434 16.844L4.87988 18.1471L7.28633 16.6177ZM35.1197 17.1483C35.5093 16.4359 36.3605 16.1983 37.0214 16.6177L39.4278 18.1471L42.3933 16.844C43.1039 16.5314 43.9145 16.8991 44.2044 17.6653C44.4942 18.4314 44.1531 19.3056 43.4428 19.6182L39.8404 21.2022C39.4387 21.379 38.9837 21.3442 38.61 21.1067L35.6101 19.2007C34.949 18.7804 34.73 17.861 35.1197 17.1483ZM0.618837 10.2441C1.11967 9.61753 1.99702 9.54655 2.57838 10.0861L4.82561 12.1736L7.54689 10.8157C8.24333 10.4682 9.07006 10.7946 9.39243 11.5453C9.7147 12.2962 9.41194 13.1876 8.71573 13.5352L5.18023 15.2988C4.68565 15.5457 4.1023 15.4578 3.68931 15.0744L0.765396 12.357C0.184274 11.817 0.118389 10.8709 0.618837 10.2441ZM41.7293 10.0861C42.3107 9.54651 43.188 9.61755 43.6888 10.2441C44.1892 10.8709 44.1235 11.817 43.5423 12.357L40.6184 15.0744C40.2054 15.4578 39.6221 15.5457 39.1274 15.2988L35.592 13.5352C34.8959 13.1876 34.5929 12.2962 34.9153 11.5453C35.2375 10.7946 36.0644 10.4682 36.7609 10.8157L39.4821 12.1736L41.7293 10.0861ZM2.97282 4.42268C3.52932 3.85311 4.4094 3.87748 4.93777 4.47731L6.43051 6.17263L8.93284 5.41763C9.67166 5.19487 10.4375 5.66087 10.6445 6.45746C10.8514 7.25416 10.4208 8.07977 9.68192 8.303L6.37441 9.30185C5.87871 9.45154 5.34663 9.2945 4.99206 8.89216L2.92215 6.54134C2.39394 5.94129 2.41649 4.99238 2.97282 4.42268ZM39.37 4.47731C39.8982 3.87746 40.7783 3.85315 41.3348 4.42268C41.8912 4.9924 41.9138 5.94129 41.3856 6.54134L39.3157 8.89216C38.9611 9.2945 38.4289 9.45156 37.9333 9.30185L34.6257 8.303C33.8868 8.07975 33.4562 7.25414 33.6632 6.45746C33.8702 5.66089 34.6361 5.19491 35.3748 5.41763L37.8772 6.17263L39.37 4.47731ZM8.50765 0.710162C8.91178 0.00685 9.76778 -0.21118 10.4201 0.22438C11.0724 0.660119 11.2728 1.5831 10.8689 2.28647L10.2211 3.41603C9.81712 4.11912 8.96089 4.33679 8.30862 3.90179C7.65627 3.46598 7.45391 2.54116 7.85808 1.83777L8.50765 0.710162ZM33.8876 0.22438C34.5399 -0.21122 35.3959 0.00685 35.8 0.710162L36.4496 1.83777C36.8537 2.54118 36.6514 3.466 35.999 3.90179C35.3468 4.33677 34.4907 4.1191 34.0866 3.41603L33.4388 2.28647C33.0349 1.5831 33.2352 0.660119 33.8876 0.22438Z" fill="url(#paint0_linear_99451_146273)"/>
  <defs>
    <linearGradient id="paint0_linear_99451_146273" x1="22.1538" y1="0" x2="22.1538" y2="29.227" gradientUnits="userSpaceOnUse">
      <stop stop-color="#232323"/>
      <stop offset="1" stop-color="#5C4739"/>
    </linearGradient>
  </defs>
</svg>`
